import { Avatar, Box, Chip, CircularProgress, Tooltip } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from '@mui/x-data-grid';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../../app/auth/service';
import { RootState } from '../../store/store';
import { EditIcon, EyeIcon, TrashIcon } from '../components/Icons';
import { ERole } from '../enums/user/ERole';
import { ETheme } from '../enums/user/ETheme';
import {
  getEmployeeIsVerifiedColor,
  getWorkModeColor,
} from '../functions/getChipColors';
import { getUserInitials } from '../functions/getUserInitials';
import { handleError } from '../functions/handleError';
import { IDepartment } from '../interfaces/department/IDepartment';
import { IUser } from '../interfaces/user/IUser';
import { darkTheme, lightTheme } from '../mui/theme';

export const useEmployeeColumns = (
  setSelectedItem: (employee: IUser) => void,
  setShowDeleteModal: (show: boolean) => void,
  isLoading: boolean,
  setIsLoading: (loading: boolean) => void,
): GridColDef[] => {
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.auth);
  const isNormalUser = userData?.role === ERole.USER;
  const navigate = useNavigate();
  return [
    { field: 'id', headerName: t('general.id'), width: 50 },
    {
      field: 'displayName',
      headerName: t('departments.employeeName'),
      flex: 1,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              style={{ marginRight: '8px' }}
              src={params.row?.profilePhoto?.thumbnail}
            >
              {getUserInitials(params.row)}
            </Avatar>
            {params.row?.displayName}
          </div>
        );
      },
    },
    {
      field: 'departments',
      headerName: t('departments.department'),
      width: 300,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        const { departments } = params?.row;
        const isEmpty = departments?.length === 0;
        return (
          <div>
            {isEmpty
              ? '-'
              : departments
                  ?.map((department: IDepartment) => department?.name)
                  .join(', ')}
          </div>
        );
      },
    },
    {
      field: 'workMode',
      headerName: t('departments.type'),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <Chip
          label={t(`workMode.${params?.row.workMode}`)}
          color={getWorkModeColor(params?.row?.workMode)}
          variant="outlined"
        />
      ),
    },
    {
      field: 'isVerified',
      headerName: t('general.verified'),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        const { isVerified, email } = params?.row;
        const isAdminOrDeveloper = [ERole.ADMIN, ERole.DEVELOPER].includes(
          userData?.role as ERole,
        );
        return (
          <Box
            sx={{
              cursor:
                isAdminOrDeveloper && !isVerified && !isNormalUser
                  ? 'pointer'
                  : 'default',
              opacity: isLoading ? 0.5 : 1,
            }}
            onClick={async () => {
              if (
                !isVerified &&
                isAdminOrDeveloper &&
                !isLoading &&
                !isNormalUser
              ) {
                setIsLoading(true);
                await AuthService.resendOtp(email)
                  .then(() => {
                    enqueueSnackbar(t('toasts.otpSent', { email }), {
                      variant: 'success',
                    });
                  })
                  .catch((err) => handleError(err))
                  .finally(() => setIsLoading(false));
              }
            }}
          >
            <Chip
              label={t(`general.${isVerified ? 'verified' : 'unverified'}`)}
              variant={'outlined'}
              color={getEmployeeIsVerifiedColor(isVerified)}
            />
            {isLoading && isAdminOrDeveloper && !isVerified && (
              <CircularProgress size={20} sx={{ marginLeft: '10px' }} />
            )}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: t('general.action'),
      width: 150,
      hideable: false,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        const { id, role } = params?.row;
        const isAdminOrDeveloper = [ERole.ADMIN, ERole.DEVELOPER].includes(
          userData?.role as ERole,
        );
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Tooltip title={t('general.preview')}>
              <div
                className={'d-flex'}
                onClick={() =>
                  navigate(
                    `/${isNormalUser ? 'user' : 'admin'}/employee/preview/${id}`,
                  )
                }
              >
                <EyeIcon
                  fill={
                    userData?.theme === ETheme.LIGHT
                      ? lightTheme.palette.text.primary
                      : darkTheme.palette.text.primary
                  }
                />
              </div>
            </Tooltip>
            {(userData?.id === id || isAdminOrDeveloper) && (
              <Tooltip title={t('general.edit')}>
                <div
                  className={'d-flex mx-3'}
                  onClick={() =>
                    navigate(
                      `/${isNormalUser ? 'user' : 'admin'}/employee/${id}/1`,
                    )
                  }
                >
                  <EditIcon
                    fill={
                      userData?.theme === ETheme.LIGHT
                        ? lightTheme.palette.text.primary
                        : darkTheme.palette.text.primary
                    }
                  />
                </div>
              </Tooltip>
            )}
            {role === ERole.USER && isAdminOrDeveloper && (
              <Tooltip title={t('general.delete')}>
                <div
                  className={'d-flex'}
                  onClick={() => {
                    setSelectedItem(params?.row);
                    setShowDeleteModal(true);
                  }}
                >
                  <TrashIcon
                    fill={
                      userData?.theme === ETheme.LIGHT
                        ? lightTheme.palette.text.primary
                        : darkTheme.palette.text.primary
                    }
                  />
                </div>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];
};
